import styled from 'styled-components';

import {Icon} from 'components/ui-kit/Icon';
import {breakpoint, colors, font} from 'theme';

export const CancelIcon = styled(Icon)`
    z-index: 2;
    cursor: pointer;
    position: absolute;
    top: 19px;
    right: 19px;
`;

export const SwitchCameraIcon = styled(Icon)`
    z-index: 2;
    position: absolute;
    top: 15px;
    left: 19px;
    cursor: pointer;
`;

export const Title = styled.div`
    font-family: ${font.family.primary};
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    text-align: center;
    color: ${colors.white};
`;

export const Description = styled.div`
    font-family: ${font.family.primary};
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    text-align: center;
    vertical-align: middle;
    color: ${colors.white};
`;

export const CameraContainer = styled.div`
    position: relative;
    width: 100%;
    margin: 0 auto;
    aspect-ratio: 1;
    background: ${colors.black};
    border-radius: 8px;
    overflow: hidden;

    @media (max-width: ${breakpoint.phone}) {
        height: 100%;
        aspect-ratio: unset;
    }
`;

export const CameraHeader = styled.div`
    box-sizing: border-box;
    padding-top: 40px;
    z-index: 1;
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    top: 0;
    left: 0;
    width: 100%;
    height: 100px;
    background: transparent;
    border-radius: 8px;

    @media (min-width: ${breakpoint.phone}) {
        padding-top: 10px;
    }
`;

const CORNER_SIZE = 24;
const CORNER_STROKE = 4;
const CORNER_COLOR_ACTIVE = '#6301FF';
const CORNER_COLOR_LOCKED = '#55CC52';

export const CameraActiveSectionCorners = styled.div<{
    $visibleSize: {width: number; height: number};
}>`
    z-index: 1;
    position: absolute;
    left: 50%;
    top: 50%;
    width: ${(p) => p.$visibleSize.width}px;
    height: ${(p) => p.$visibleSize.height}px;
    transform: translate(-50%, -50%);
    pointer-events: none;
`;

export const CornerMarker = styled.div<{
    $scanLocked: boolean;
    $position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}>`
    position: absolute;
    width: ${CORNER_SIZE}px;
    height: ${CORNER_SIZE}px;
    border-color: ${(p) => (p.$scanLocked ? CORNER_COLOR_LOCKED : CORNER_COLOR_ACTIVE)};
    border-style: solid;
    border-width: 0;
    ${(p) => {
        switch (p.$position) {
            case 'top-left':
                return `
                    left: -3px;
                    top: -3px;
                    border-left-width: ${CORNER_STROKE}px;
                    border-top-width: ${CORNER_STROKE}px;
                    border-top-left-radius: 4px;
                `;
            case 'top-right':
                return `
                    right: -3px;
                    top: -3px;
                    border-right-width: ${CORNER_STROKE}px;
                    border-top-width: ${CORNER_STROKE}px;
                    border-top-right-radius: 4px;
                `;
            case 'bottom-left':
                return `
                    left: -3px;
                    bottom: -3px;
                    border-left-width: ${CORNER_STROKE}px;
                    border-bottom-width: ${CORNER_STROKE}px;
                    border-bottom-left-radius: 4px;
                `;
            case 'bottom-right':
                return `
                    right: -3px;
                    bottom: -3px;
                    border-right-width: ${CORNER_STROKE}px;
                    border-bottom-width: ${CORNER_STROKE}px;
                    border-bottom-right-radius: 4px;
                `;
        }
    }}
`;

export const SuccessCheck = styled.div<{
    $scanLocked: boolean;
    $visibleSize: {width: number; height: number};
}>`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${(p) => p.$visibleSize.width * 0.8}px;
    height: ${(p) => p.$visibleSize.height * 0.8}px;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: ${(p) => (p.$scanLocked ? 1 : 0)};
    transition: opacity 0.2s ease-in-out;
    &::after {
        content: '';
        width: 28%;
        height: 50%;
        margin-bottom: 8%;
        margin-left: 4%;
        border-right: 5px solid ${CORNER_COLOR_LOCKED};
        border-bottom: 5px solid ${CORNER_COLOR_LOCKED};
        transform: rotate(45deg);
    }
`;

export const CameraOverlay = styled.div<{$visibleSize: {width: number; height: number}}>`
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    overflow: hidden;
    pointer-events: none;

    mask-image: linear-gradient(
            to bottom,
            black 0%,
            black calc(50% - ${(p) => p.$visibleSize.height / 2}px),
            transparent calc(50% - ${(p) => p.$visibleSize.height / 2}px),
            transparent calc(50% + ${(p) => p.$visibleSize.height / 2}px),
            black calc(50% + ${(p) => p.$visibleSize.height / 2}px),
            black 100%
        ),
        linear-gradient(
            to right,
            black 0%,
            black calc(50% - ${(p) => p.$visibleSize.width / 2}px),
            transparent calc(50% - ${(p) => p.$visibleSize.width / 2}px),
            transparent calc(50% + ${(p) => p.$visibleSize.width / 2}px),
            black calc(50% + ${(p) => p.$visibleSize.width / 2}px),
            black 100%
        );

    mask-size: 100% 100%;
    -webkit-mask-size: 100% 100%;
    mask-repeat: no-repeat;
    -webkit-mask-repeat: no-repeat;
`;

export const Video = styled.video<{$visible?: boolean}>`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: ${(p) => (p.$visible ? 1 : 0)};
`;

export const CameraPlaceholder = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${colors.white};
    font-size: 14px;
`;

export const CameraError = styled.div`
    padding: 16px;
    color: ${colors.danger};
    text-align: center;
    font-size: 14px;
    position: relative;
`;

export const ScanCanvas = styled.canvas`
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    overflow: hidden;
    pointer-events: none;
`;
